import React, { useState, useEffect, useCallback } from 'react';
import { ROLE_OPTIONS } from '../../../constants/roles';
import { getRoleConfig } from '../../../utils/roleConfig';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LockResetIcon from '@mui/icons-material/LockReset';
import PersonIcon from '@mui/icons-material/Person';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import api from '../../../services/api';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  const [roleFilter, setRoleFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog states
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openResetDialog, setOpenResetDialog] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  
  // Form states
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: '',
    fullName: '',
    phone: '',
    clientIds: []
  });
  const [resetPasswordData, setResetPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  
  // Client states
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);

  const roles = ROLE_OPTIONS;

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage
      };
      
      // Add role filter if not 'all'
      if (roleFilter !== 'all') {
        params.role = roleFilter;
      }
      if (searchQuery.trim() !== '') {
        params.search = searchQuery.trim();
      }
      
      const response = await api.get('/users', { params });
      
      setUsers(response.data.users);
      setTotalUsers(response.data.count);
    } catch (error) {
      console.error('Error fetching users:', error);
      setAlert({
        open: true,
        message: 'Gagal memuat data pengguna',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, roleFilter, searchQuery]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearchQuery(searchInput);
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchClients = async () => {
    setClientsLoading(true);
    try {
      const response = await api.get('/clients');
      setClients(response.data.clients || response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setAlert({
        open: true,
        message: 'Gagal memuat data client',
        severity: 'error'
      });
    } finally {
      setClientsLoading(false);
    }
  };

  const fetchUserClients = async (userId) => {
    try {
      const response = await api.get(`/user-clients/user/${userId}`);
      return response.data.clients.map(client => client.id);
    } catch (error) {
      console.error('Error fetching user clients:', error);
      return [];
    }
  };

  const handleOpenAddDialog = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      role: '',
      fullName: '',
      phone: '',
      clientIds: []
    });
    fetchClients();
    setOpenAddDialog(true);
  };

  const handleOpenEditDialog = async (user) => {
    setSelectedUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      role: user.role,
      fullName: user.fullName || '',
      phone: user.phone || '',
      clientIds: []
    });
    
    fetchClients();
    
    // Fetch user's current client associations
    if (user.role === 'customer') {
      const userClientIds = await fetchUserClients(user.id);
      setFormData(prev => ({ ...prev, clientIds: userClientIds }));
    }
    
    setOpenEditDialog(true);
  };

  const handleOpenDeleteDialog = (user) => {
    setSelectedUser(user);
    setOpenDeleteDialog(true);
  };

  const handleOpenResetDialog = (user) => {
    setSelectedUser(user);
    setResetPasswordData({
      newPassword: '',
      confirmPassword: ''
    });
    setOpenResetDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenAddDialog(false);
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
    setOpenResetDialog(false);
    setSelectedUser(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleResetPasswordChange = (e) => {
    const { name, value } = e.target;
    setResetPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddUser = async () => {
    if (!formData.username || !formData.email || !formData.password || !formData.role) {
      setAlert({
        open: true,
        message: 'Harap isi semua field yang wajib',
        severity: 'error'
      });
      return;
    }
    
    // Validate customer role has clients
    if (formData.role === 'customer' && (!formData.clientIds || formData.clientIds.length === 0)) {
      setAlert({
        open: true,
        message: 'User dengan role Customer harus memiliki minimal satu client',
        severity: 'error'
      });
      return;
    }
    
    setDialogLoading(true);
    
    try {
      await api.post('/users/create', formData);
      
      setAlert({
        open: true,
        message: 'Pengguna berhasil ditambahkan',
        severity: 'success'
      });
      
      handleCloseDialog();
      fetchUsers();
    } catch (error) {
      console.error('Error adding user:', error);
      setAlert({
        open: true,
        message: error.response?.data?.message || 'Gagal menambahkan pengguna',
        severity: 'error'
      });
    } finally {
      setDialogLoading(false);
    }
  };

  const handleEditUser = async () => {
    if (!formData.username || !formData.email || !formData.role) {
      setAlert({
        open: true,
        message: 'Harap isi semua field yang wajib',
        severity: 'error'
      });
      return;
    }
    
    // Validate customer role has clients
    if (formData.role === 'customer' && (!formData.clientIds || formData.clientIds.length === 0)) {
      setAlert({
        open: true,
        message: 'User dengan role Customer harus memiliki minimal satu client',
        severity: 'error'
      });
      return;
    }
    
    setDialogLoading(true);
    
    try {
      await api.put(`/users/${selectedUser.id}`, {
        username: formData.username,
        email: formData.email,
        role: formData.role,
        fullName: formData.fullName,
        phone: formData.phone,
        clientIds: formData.clientIds
      });
      
      setAlert({
        open: true,
        message: 'Pengguna berhasil diperbarui',
        severity: 'success'
      });
      
      handleCloseDialog();
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      setAlert({
        open: true,
        message: error.response?.data?.message || 'Gagal memperbarui pengguna',
        severity: 'error'
      });
    } finally {
      setDialogLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    setDialogLoading(true);
    
    try {
      await api.delete(`/users/${selectedUser.id}`);
      
      setAlert({
        open: true,
        message: 'Pengguna berhasil dihapus',
        severity: 'success'
      });
      
      handleCloseDialog();
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      setAlert({
        open: true,
        message: error.response?.data?.message || 'Gagal menghapus pengguna',
        severity: 'error'
      });
    } finally {
      setDialogLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordData.newPassword || resetPasswordData.newPassword !== resetPasswordData.confirmPassword) {
      setAlert({
        open: true,
        message: 'Password baru dan konfirmasi tidak cocok',
        severity: 'error'
      });
      return;
    }
    
    setDialogLoading(true);
    
    try {
      await api.post(`/admin/users/${selectedUser.id}/reset-password`, {
        newPassword: resetPasswordData.newPassword
      });
      
      setAlert({
        open: true,
        message: 'Password berhasil direset',
        severity: 'success'
      });
      
      handleCloseDialog();
    } catch (error) {
      console.error('Error resetting password:', error);
      setAlert({
        open: true,
        message: error.response?.data?.message || 'Gagal mereset password',
        severity: 'error'
      });
    } finally {
      setDialogLoading(false);
    }
  };

  const handleToggleUserStatus = async (user) => {
    try {
      await api.patch(`/admin/users/${user.id}/status`, {
        isActive: !user.isActive
      });
      
      setAlert({
        open: true,
        message: `Pengguna berhasil ${user.isActive ? 'dinonaktifkan' : 'diaktifkan'}`,
        severity: 'success'
      });
      
      fetchUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
      setAlert({
        open: true,
        message: error.response?.data?.message || 'Gagal mengubah status pengguna',
        severity: 'error'
      });
    }
  };

  const handleCloseAlert = () => {
    setAlert(prev => ({ ...prev, open: false }));
  };

  const getRoleLabel = (roleValue) => {
    const role = roles.find(r => r.value === roleValue);
    return role ? role.label : roleValue;
  };

  // Role chip colors come from the shared roleConfig (single source of truth).

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          MANAJEMEN PENGGUNA
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            variant="outlined"
            placeholder="Cari username / email / nama"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ minWidth: 260 }}
          />
          <FormControl variant="outlined" size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="role-filter-label">FILTER ROLE</InputLabel>
            <Select
              labelId="role-filter-label"
              id="role-filter"
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setPage(0); // Reset to first page when filter changes
              }}
              label="Filter Role"
            >
              <MenuItem value="all">SEMUA ROLE</MenuItem>
              {ROLE_OPTIONS.map((r) => (
                <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
          >
            TAMBAH USER BARU
          </Button>
        </Box>
      </Box>
      
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>USERNAME</TableCell>
                <TableCell>EMAIL</TableCell>
                <TableCell>NAMA LENGKAP</TableCell>
                <TableCell>ROLE</TableCell>
                <TableCell>STATUS</TableCell>
                <TableCell align="center">AKSI</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Tidak ada data pengguna
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.fullName || '-'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={getRoleLabel(user.role)}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          letterSpacing: 0.3,
                          bgcolor: getRoleConfig(user.role).bg,
                          color: getRoleConfig(user.role).color
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={user.isActive ? 'Aktif' : 'Nonaktif'} 
                        color={user.isActive ? 'success' : 'error'} 
                        size="small"
                        onClick={() => handleToggleUserStatus(user)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit User">
                        <IconButton size="small" onClick={() => handleOpenEditDialog(user)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reset Password">
                        <IconButton size="small" onClick={() => handleOpenResetDialog(user)}>
                          <LockResetIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={user.isActive ? "Non-Aktifkan User" : "Aktifkan User"}>
                        <IconButton 
                          size="small" 
                          onClick={() => handleToggleUserStatus(user)}
                          color={user.isActive ? "warning" : "success"}
                        >
                          {user.isActive ? 
                            <PersonOffIcon fontSize="small" /> : 
                            <PersonIcon fontSize="small" />
                          }
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Hapus User">
                        <IconButton 
                          size="small" 
                          onClick={() => handleOpenDeleteDialog(user)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalUsers}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      
      {/* Add User Dialog */}
      <Dialog open={openAddDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>TAMBAH USER BARU</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Username"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
            />
            <FormControl fullWidth margin="normal" required>
              <InputLabel>ROLE</InputLabel>
              <Select
                name="role"
                value={formData.role}
                label="ROLE"
                onChange={handleInputChange}
              >
                {roles.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    {role.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              margin="normal"
              fullWidth
              label="Nama Lengkap"
              name="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
            />
            <TextField
              margin="normal"
              fullWidth
              label="Nomor Telepon"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
            />
            {formData.role === 'customer' && (
              <FormControl fullWidth margin="normal" required>
                <InputLabel>CLIENT</InputLabel>
                <Select
                  multiple
                  name="clientIds"
                  value={formData.clientIds}
                  label="CLIENT"
                  onChange={(e) => setFormData(prev => ({ ...prev, clientIds: e.target.value }))}
                  renderValue={(selected) => {
                    const selectedClients = clients.filter(client => selected.includes(client.id));
                    return selectedClients.map(client => client.name).join(', ');
                  }}
                  disabled={clientsLoading}
                >
                  {clientsLoading ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} />
                      Loading clients...
                    </MenuItem>
                  ) : (
                    clients.map((client) => (
                      <MenuItem key={client.id} value={client.id}>
                        {client.name} {client.companyName && `(${client.companyName})`}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Batal</Button>
          <Button 
            onClick={handleAddUser} 
            variant="contained" 
            disabled={dialogLoading}
          >
            {dialogLoading ? <CircularProgress size={24} /> : 'Simpan'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Edit User Dialog */}
      <Dialog open={openEditDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>EDIT USER</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Username"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
            />
            <FormControl fullWidth margin="normal" required>
              <InputLabel>ROLE</InputLabel>
              <Select
                name="role"
                value={formData.role}
                label="ROLE"
                onChange={handleInputChange}
              >
                {roles.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    {role.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              margin="normal"
              fullWidth
              label="Nama Lengkap"
              name="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
            />
            <TextField
              margin="normal"
              fullWidth
              label="Nomor Telepon"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
            />
            {formData.role === 'customer' && (
              <FormControl fullWidth margin="normal" required>
                <InputLabel>CLIENT</InputLabel>
                <Select
                  multiple
                  name="clientIds"
                  value={formData.clientIds}
                  label="CLIENT"
                  onChange={(e) => setFormData(prev => ({ ...prev, clientIds: e.target.value }))}
                  renderValue={(selected) => {
                    const selectedClients = clients.filter(client => selected.includes(client.id));
                    return selectedClients.map(client => client.name).join(', ');
                  }}
                  disabled={clientsLoading}
                >
                  {clientsLoading ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} />
                      Loading clients...
                    </MenuItem>
                  ) : (
                    clients.map((client) => (
                      <MenuItem key={client.id} value={client.id}>
                        {client.name} {client.companyName && `(${client.companyName})`}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Batal</Button>
          <Button 
            onClick={handleEditUser} 
            variant="contained" 
            disabled={dialogLoading}
          >
            {dialogLoading ? <CircularProgress size={24} /> : 'Simpan'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete User Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDialog}>
        <DialogTitle>HAPUS USER</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Apakah Anda yakin ingin menghapus user <strong>{selectedUser?.username}</strong>? Tindakan ini tidak dapat dibatalkan.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Batal</Button>
          <Button 
            onClick={handleDeleteUser} 
            color="error"
            disabled={dialogLoading}
          >
            {dialogLoading ? <CircularProgress size={24} /> : 'Hapus'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Reset Password Dialog */}
      <Dialog open={openResetDialog} onClose={handleCloseDialog}>
        <DialogTitle>RESET PASSWORD</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Reset password untuk user <strong>{selectedUser?.username}</strong>
          </DialogContentText>
          <TextField
            margin="normal"
            required
            fullWidth
            label="Password Baru"
            name="newPassword"
            type="password"
            value={resetPasswordData.newPassword}
            onChange={handleResetPasswordChange}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            label="Konfirmasi Password Baru"
            name="confirmPassword"
            type="password"
            value={resetPasswordData.confirmPassword}
            onChange={handleResetPasswordChange}
            error={resetPasswordData.newPassword !== resetPasswordData.confirmPassword && resetPasswordData.confirmPassword !== ''}
            helperText={resetPasswordData.newPassword !== resetPasswordData.confirmPassword && resetPasswordData.confirmPassword !== '' ? 'Password tidak cocok' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Batal</Button>
          <Button 
            onClick={handleResetPassword} 
            variant="contained"
            disabled={dialogLoading}
          >
            {dialogLoading ? <CircularProgress size={24} /> : 'Reset Password'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
        <Alert onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagement;
